"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useNativeDialogAsModal = void 0;
const utils_1 = require("./utils");
/**
 * Improves native dialog element by providing following features:
 *
 * - Default close button logic
 * - Close modal by clicking the background
 * - Scroll-lock on html body when modal is opened
 *
 * @param {HTMLDialogElement} dialog Dialog element.
 * @param {object} [options] Dialog options.
 * @param {boolean} [options.closeOnBackgroundClick=true] Whether dialog is closed when background is clicked. Default is true.
 * @param {string} [options.dialogContentSelector] Set selector which is used to check if click was out of bounds. Default is `[data-dialog-content]`.
 * @param {boolean} [options.registerCloseButtons=true] Whether default logic for close buttons is used. Default is true.
 * @param {string} [options.closeButtonSelector] HTML query selector for close buttons. Default is `[data-dialog-close]`.
 * @param {boolean} [options.preventBodyScroll=true] Whether body scrolling is diabled. Default is true.
 * @param {string} [options.bodyPreventScrollClass] CSS class that is added to body when scrolling is prevented. Default is `dialog-open`.
 */
function useNativeDialogAsModal(dialog, options = {}) {
    options = {
        registerCloseButtons: true,
        closeButtonSelector: '[data-dialog-close]',
        closeOnBackgroundClick: true,
        dialogContentSelector: '[data-dialog-content]',
        preventBodyScroll: true,
        bodyPreventScrollClass: 'dialog-open',
        ...options
    };
    // Close modal when user clicks close button inside modal
    if (options.registerCloseButtons) {
        const closeHandler = () => {
            dialog.close();
        };
        const closeButtons = dialog.querySelectorAll(options.closeButtonSelector);
        closeButtons.forEach(button => {
            button.addEventListener('click', closeHandler);
        });
    }
    // Close modal when user clicks outside dialog content area
    if (options.closeOnBackgroundClick) {
        const closeOnClickHandler = (e) => {
            if (!e.target.closest(options.dialogContentSelector)) {
                dialog.close();
            }
        };
        dialog.addEventListener('click', closeOnClickHandler);
    }
    // Prevent body scroll when dialog is open
    if (options.preventBodyScroll) {
        let scrollY = null;
        const observer = new MutationObserver((mutationList, observer) => {
            for (const mutation of mutationList) {
                if (mutation.type === 'attributes') {
                    if (dialog.open) {
                        if (!document.body.classList.contains(options.bodyPreventScrollClass)) {
                            scrollY = window.scrollY;
                            document.body.classList.add(options.bodyPreventScrollClass);
                            window.scrollTo({ left: window.scrollX, top: scrollY });
                        }
                    }
                    else {
                        if (scrollY && document.body.classList.contains(options.bodyPreventScrollClass)) {
                            document.body.classList.remove(options.bodyPreventScrollClass);
                            window.scrollTo({ left: window.scrollX, top: scrollY });
                            scrollY = null;
                        }
                    }
                }
            }
        });
        observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
        // If focus moves from opened dialog to other element on page, scrollY value seems to reset to 0
        // As a workaround, always reset scroll position after blur event occures in dialog inputs
        let posTimeout = null;
        const setScrollPositionAfterBlur = (e) => {
            if (posTimeout) {
                clearTimeout(posTimeout);
            }
            posTimeout = setTimeout(() => {
                if (scrollY && document.body.classList.contains(options.bodyPreventScrollClass)) {
                    window.scrollTo({ left: window.scrollX, top: scrollY });
                }
            }, 0);
        };
        const focusableChildren = (0, utils_1.getFocusableChildren)(dialog, { checkVisibility: false });
        focusableChildren.forEach(child => {
            child.addEventListener('blur', setScrollPositionAfterBlur);
        });
    }
}
exports.useNativeDialogAsModal = useNativeDialogAsModal;
